<template>
  <div
    class="ooloja-card-default__body bg-white p-6 rounded-lg shadow-sm border border-gray-200"
  >
    <div class="flex flex-col md:flex-row items-center justify-between mb-4">
      <div class="text-center md:text-left mb-4 md:mb-0">
        <h3 class="text-lg font-semibold text-gray-800">
          Top 10 Ticket Médio por Produto
          <span class="block text-sm text-gray-400">Dados são dos ultimos 12 meses</span>
        </h3>
      </div>

      <div class="flex justify-between space-x-2">
        <el-tooltip content="Exportar CSV" placement="top">
          <button @click="exportToCSV" class="bg-transparent text-gray-500">
            <Icon name="teenyicons:csv-outline" size="30" />
          </button>
        </el-tooltip>
        <el-tooltip content="Exporta PDF" placement="top">
          <button @click="exportToPDF" class="bg-transparent text-gray-500">
            <Icon name="teenyicons:pdf-outline" size="30" />
          </button>
        </el-tooltip>
        <el-tooltip content="Expandir tabela" placement="top">
          <button @click="openModal" class="bg-transparent text-gray-500">
            <Icon name="hugeicons:square-arrow-expand-01" size="30" />
          </button>
        </el-tooltip>
      </div>
    </div>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <DashboardTicketMediaTabela :ticket-medio-data="ticketMedioData" />

      <div class="mb-6">
        <Bar :data="chartData" :options="chartOptions" class="h-64" />
      </div>
    </div>
  </div>

  <TableModal ref="tableModal">
    <DashboardTicketMediaTabela :ticket-medio-data="ticketMedioData" />
  </TableModal>
</template>

<script setup>
import { ref, computed } from "vue";
import TableModal from "./TableModal.vue";
import DashboardTicketMediaTabela from "./DashboardTicketMediaTabela.vue";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const props = defineProps({
  ticketMedioData: {
    type: Array,
    required: true,
  },
});

const tableModal = ref(null);
const sortKey = ref("ticket_medio");
const sortOrder = ref("desc");

// Formatar valores monetários
const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

// Ordenação
const sortedData = computed(() => {
  const sorted = [...props.ticketMedioData];
  if (sortKey.value) {
    sorted.sort((a, b) => {
      const valueA = a[sortKey.value];
      const valueB = b[sortKey.value];
      if (sortOrder.value === "asc") {
        return valueA > valueB ? 1 : -1;
      }
      return valueA < valueB ? 1 : -1;
    });
  }
  return sorted;
});

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

// Dados do gráfico
const chartData = computed(() => ({
  labels: props.ticketMedioData.map(
    (item) => item.titulo.slice(0, 20) + (item.titulo.length > 20 ? "..." : "")
  ),
  datasets: [
    {
      label: "Ticket Médio (R$)",
      data: props.ticketMedioData.map((item) => item.ticket_medio),
      backgroundColor: "rgba(59, 130, 246, 0.5)",
      borderColor: "rgba(59, 130, 246, 1)",
      borderWidth: 1,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "top",
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${formatCurrency(context.raw)}`,
        title: function (context) {
          const item = props.ticketMedioData[context[0].dataIndex];
          return `${item.titulo}`;
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: (value) => formatCurrency(value),
      },
    },
    x: {
      ticks: {
        autoSkip: false,
        maxRotation: 45,
        minRotation: 45,
      },
    },
  },
};

// Exportar para CSV
const exportToCSV = () => {
  const headers = ["Produto", "Receita Total", "Quantidade Total", "Ticket Médio"];
  const data = props.ticketMedioData.map((item) => [
    item.titulo,
    formatCurrency(item.receita_total),
    item.quantidade_total,
    formatCurrency(item.ticket_medio),
  ]);

  const csvContent = [
    headers.join(","),
    ...data.map((row) => row.map((cell) => `"${cell}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "ticket_medio.csv";
  link.click();
};

// Exportar para PDF
const exportToPDF = async () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 14;
  const systemVersion = "Versão 1.0"; // Substitua pela versão real
  const website = "www.ooloja.com.br";

  // Formatar data e hora atual
  const now = new Date();
  const dateText = now.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  // Carregar logo
  let logoImg = null;
  try {
    const logoUrl = "/ooadmin.png";
    const response = await fetch(logoUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const blob = await response.blob();
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    await new Promise((resolve) => (reader.onload = resolve));
    logoImg = reader.result;
  } catch (error) {
    console.error("Erro ao carregar logo:", error.message);
  }

  // Configurar tabela
  autoTable(doc, {
    head: [["Produto", "Receita Total", "Quantidade Total", "Ticket Médio"]],
    body: props.ticketMedioData.map((item) => [
      item.titulo,
      formatCurrency(item.receita_total),
      item.quantidade_total,
      formatCurrency(item.ticket_medio),
    ]),
    startY: 30,
    styles: { fontSize: 10, cellPadding: 3 },
    headStyles: { fillColor: [100, 116, 139], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [243, 244, 246] },
    didDrawPage: (data) => {
      // Cabeçalho
      // Data e hora
      doc.setFont("Times", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Gerado em: ${dateText}`, margin, 15);

      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text("Ticket Médio por Produto", margin, 22);

      doc.setFontSize(10);
      doc.text("Dados referentes aos últimos 12 meses", margin, 27);

      if (logoImg) {
        doc.addImage(logoImg, "PNG", pageWidth - 40 - margin, 7, 40, 15);
      } else {
        doc.setFontSize(8);
        doc.text("[Logo]", pageWidth - 30 - margin, 15);
      }
      // Rodapé
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const footerText = `${website} - ${systemVersion}`;
      doc.text(footerText, margin, pageHeight - 10);
      const pageStr = `Página ${data.pageNumber} de ${doc.internal.getNumberOfPages()}`;
      doc.text(pageStr, pageWidth - margin - doc.getTextWidth(pageStr), pageHeight - 10);
    },
    margin: { top: 30 },
  });

  doc.save("ticket_medio.pdf");
};

// Função para abrir o modal
const openModal = () => {
  tableModal.value.openModal();
};
</script>

<style></style>
